import { Grid } from '@mui/material';
import { Language } from '@nx-smartmonkey/shared/domain';
import { getWeekDates } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalLoader as Loader, RoutalPalette, VSpacer } from '@nx-smartmonkey/ui';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import CalendarIcon from '../../../assets/img/CalendarIcon.svg';
import { RefreshIcon } from '../../../assets/svg/RefreshIcon';
import { LoadingButton } from '../../../components/LoadingButton';
import * as routing from '../../../constants/Routing';
import { Route } from '../../../domain/route/Route';
import { useRetrieveActiveLanguage } from '../../../hooks/translations/useRetrieveActiveLanguage';
import Button from '../../../react-components/Button';
import { Text } from '../../../react-components/Text';
import { usePermissions } from '../hooks/usePermissions';
import { RouteCreation } from '../routes-list/route-creation/RouteCreation';
import { RoutePill } from '../routes-list/RoutePill';
import { useSearchRoutes } from './hooks/useSearchRoutes';

const MAX_ROUTES_TO_DISPLAY = 3;

const DayPill = styled.span<{ isActive: boolean; dayHasRoutes: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  border-radius: 4px;
  background-color: ${(props) => (props.isActive ? RoutalPalette.primary60 : `transparent`)};
  color: ${RoutalPalette.neutral02n};

  font-size: 12px;
  font-weight: 500;

  height: 28px;
  min-width: 24px;
  padding: 0 4px;

  ${(props) =>
    props.dayHasRoutes &&
    `
    ::after {
      position: absolute;
      bottom: 4px;
      content: '';
      display: block;
      width: 3px;
      height: 3px;
      border-radius: 50%;
      background-color: ${RoutalPalette.neutral02};
    }
  `}
`;

const HOME_CALENDAR_ROUTES_PAGINATION_LIMIT = 100;

interface HomeCalendarProps {
  refreshTrigger: number;
  parentIsScrolling?: boolean;
}

export const HomeCalendar = ({ refreshTrigger, parentIsScrolling = false }: HomeCalendarProps) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { activeLanguage } = useRetrieveActiveLanguage();

  const { canCreateRoutes } = usePermissions();

  const [isRouteCreationOpen, setIsRouteCreationOpen] = useState<boolean>(false);

  const language = activeLanguage ?? Language.getDefaultLanguage().value;
  const today = moment().locale(language);
  const [activeDay, setActiveDay] = useState<Date>(today.toDate());

  const weekDays = getWeekDates({
    date: new Date(),
    language,
  });

  const {
    data: calendarRoutes,
    run: searchRoutes,
    isLoading: isSearchingRoutes,
  } = useSearchRoutes({
    startDay: weekDays[0],
    endDay: weekDays[6],
    limit: HOME_CALENDAR_ROUTES_PAGINATION_LIMIT,
  });

  useEffect(() => {
    searchRoutes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTrigger]);

  const filterRoutesByActiveDay = (route: Route) => {
    return filterRoutesByDay(route, activeDay);
  };

  const filterRoutesByDay = (route: Route, day: Date) => {
    const routeDay = moment(route.plan_execution_date).locale(language);
    return routeDay.isSame(day, `day`);
  };

  const routesOfTheDay = (calendarRoutes?.docs ?? []).filter(filterRoutesByActiveDay);

  return (
    <>
      <div
        style={{
          padding: `20px 24px`,
        }}
      >
        <Grid
          container
          direction="column"
          style={{
            borderRadius: `10px`,
            padding: `12px`,
            backgroundColor: RoutalPalette.primary40,
          }}
        >
          <Grid container direction="row" gap="6px" onClick={() => navigate(routing.PRIVATE_ROUTES.ROUTES)}>
            <img src={CalendarIcon} alt="" style={{ width: `14px` }} />

            <Text
              variant="body"
              intlId="drivers.home_calendar.title"
              style={{
                color: RoutalPalette.neutral05,
              }}
            />
          </Grid>

          <VSpacer medium />

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            style={{
              padding: `6px 20px`,
            }}
          >
            {weekDays.map((day) => {
              const m = moment(day).locale(language);
              const isActive = m.isSame(activeDay, `day`);

              const dayString = m.format(`ddd`);
              const dayCapitalized = dayString.charAt(0).toUpperCase() + dayString.slice(1);
              const readableDay = dayCapitalized.replace(/\./g, ``);

              const dayHasRoutes =
                (calendarRoutes?.docs ?? []).filter((route) => filterRoutesByDay(route, day)).length > 0;

              return (
                <DayPill
                  key={day.getTime()}
                  isActive={isActive}
                  dayHasRoutes={dayHasRoutes}
                  onClick={() => setActiveDay(day)}
                >
                  {readableDay}
                </DayPill>
              );
            })}
          </Grid>

          <Grid container direction="column" style={{ paddingTop: `12px`, minHeight: `100px` }}>
            {isSearchingRoutes ? (
              <Grid container direction="column" wrap="nowrap" alignItems="center" justifyContent="center">
                <Loader width={40} />
              </Grid>
            ) : (
              <Grid container direction="column">
                {routesOfTheDay.length > 0 ? (
                  <>
                    {routesOfTheDay.slice(0, MAX_ROUTES_TO_DISPLAY).map((route, index, array) => {
                      return (
                        <RoutePill
                          key={route.id}
                          route={route}
                          disabled={parentIsScrolling}
                          style={{
                            borderRadius: `10px`,
                            marginBottom: index === array.length - 1 ? 0 : `6px`,
                          }}
                          onDelete={() => {
                            searchRoutes();
                          }}
                        />
                      );
                    })}
                    {routesOfTheDay.length > MAX_ROUTES_TO_DISPLAY && (
                      <Button
                        variant="flat"
                        intlId="drivers.home_calendar.view_more_routes"
                        intlValues={{
                          num: `+${routesOfTheDay.length - MAX_ROUTES_TO_DISPLAY}`,
                        }}
                        style={{
                          height: `35px`,
                          marginTop: `14px`,
                          color: RoutalPalette.primary00,
                          border: `1px solid ${RoutalPalette.primary00}`,
                        }}
                        onClick={() => navigate(routing.PRIVATE_ROUTES.ROUTES)}
                      />
                    )}
                  </>
                ) : (
                  <Grid
                    container
                    direction="column"
                    justifyContent="center"
                    alignItems="center"
                    gap="18px"
                    style={{ paddingTop: `4px` }}
                  >
                    <Text
                      variant="body"
                      style={{ color: RoutalPalette.neutral05 }}
                      intlId="drivers.home_calendar.no_routes_this_day"
                    />

                    {canCreateRoutes ? (
                      <LoadingButton
                        id="create-new-route-button"
                        style={{
                          height: `42px`,
                          width: `calc(100% - 24px)`,
                          fontSize: `14px`,
                          fontWeight: `normal`,
                          borderRadius: `10px`,
                          margin: 0,
                          color: RoutalPalette.neutral00,
                          backgroundColor: RoutalPalette.primary60,
                          border: 0,
                        }}
                        onClick={() => {
                          setIsRouteCreationOpen(true);
                        }}
                      >
                        {intl.formatMessage({ id: `drivers.route.create_new` })}
                      </LoadingButton>
                    ) : (
                      <Button
                        onClick={() => searchRoutes()}
                        variant="flat"
                        style={{
                          width: `100%`,
                          height: `52px`,
                          padding: `4px 24px`,
                          boxSizing: `border-box`,
                          backgroundColor: RoutalPalette.primary60,
                        }}
                      >
                        <RefreshIcon color={RoutalPalette.neutral00} style={{ width: `12px`, height: `12px` }} />
                        <HSpacer small />
                        <Text
                          style={{ color: RoutalPalette.neutral00 }}
                          intlId="driver.stops.no_stops_list.refresh_page"
                        />
                      </Button>
                    )}
                  </Grid>
                )}
              </Grid>
            )}
          </Grid>
        </Grid>
      </div>

      <RouteCreation
        isOpen={isRouteCreationOpen}
        initialDate={activeDay}
        onClose={() => {
          setIsRouteCreationOpen(false);
        }}
      />
    </>
  );
};
